/**
 * BaaS account hierarchy for processing home Resumo cards.
 * Pinned Root / Operating / Overdraft / Program balances come from
 * `GET v1/accounts/internal-transfer-options` — same source as Contas pinned panel.
 * Never build Resumo from `fetchBaasAccounts` (that list excludes virtual shells).
 */

import { baasProxy } from "./baasConsoleClient";
import { isValutSegregationWalletInstance } from "./baasAccountsListApi";
import { fetchInternalTransferOptions } from "./baasInternalTransfersApi";

export const BAAS_ACCOUNT_TYPES = {
  ROOT: "root",
  OPERATIONAL: "operational",
  OVERDRAFT: "overdraft",
  RESERVE: "reserve",
};

/** Map internal-transfer-options `role` → Resumo card type. */
export const BAAS_PINNED_ROLE_TO_TYPE = {
  root: BAAS_ACCOUNT_TYPES.ROOT,
  operating: BAAS_ACCOUNT_TYPES.OPERATIONAL,
  overdraft_reserve: BAAS_ACCOUNT_TYPES.OVERDRAFT,
  program_reserve: BAAS_ACCOUNT_TYPES.RESERVE,
};

export const getBaasBalanceTotal = (balance) =>
  Number(balance?.available ?? 0) + Number(balance?.blocked ?? 0);

export const getBaasAccountBalanceSummary = (balances = []) =>
  balances.map((balance) => {
    const currency = String(balance.currency || "").toUpperCase();
    return {
      currency,
      available: Number(balance.available ?? 0),
      blocked: Number(balance.blocked ?? 0),
      total: getBaasBalanceTotal(balance),
    };
  });

const emptyHierarchy = () => ({
  clientName: "",
  rootAccount: null,
  subAccounts: [],
});

const mapPinnedOptionToHierarchyAccount = (account, type) => {
  if (!account?.id) return null;
  return {
    type,
    id: String(account.id).trim(),
    label: account.name || account.role || account.id,
    status: "active",
    role: String(account.role || "").toLowerCase(),
    balances: Array.isArray(account.balances) ? account.balances : [],
  };
};

/**
 * Build Resumo hierarchy from internal-transfer-options payload (already mapped).
 * Pure — unit-tested. Balances are major units (same as Contas pinned panel).
 */
export const buildBaasAccountHierarchyFromTransferOptions = (
  options,
  { clientName = "" } = {},
) => {
  const byRole = new Map();
  const ingest = (list = []) => {
    (Array.isArray(list) ? list : []).forEach((account) => {
      const role = String(account?.role || "").toLowerCase();
      if (!account?.id || !BAAS_PINNED_ROLE_TO_TYPE[role]) return;
      if (!byRole.has(role)) byRole.set(role, account);
    });
  };

  ingest(options?.sourceAccounts);
  ingest(options?.destinationAccounts);
  ingest(options?.virtualAccounts);

  const root = byRole.get("root");
  const rootAccount = root
    ? mapPinnedOptionToHierarchyAccount(root, BAAS_ACCOUNT_TYPES.ROOT)
    : null;

  const subAccounts = ["operating", "overdraft_reserve", "program_reserve"]
    .map((role) => {
      const account = byRole.get(role);
      if (!account) return null;
      return mapPinnedOptionToHierarchyAccount(account, BAAS_PINNED_ROLE_TO_TYPE[role]);
    })
    .filter(Boolean);

  return {
    clientName: clientName || root?.name || "",
    rootAccount,
    subAccounts,
  };
};

/**
 * Instance-pinned shell account ids (operating / overdraft / program, plus virtual flags).
 * Used by Stables to never treat reserve AccountBalance as wallet saldo.
 */
export const collectPinnedShellAccountIds = (options) => {
  const ids = new Set();
  const consider = (list = []) => {
    (Array.isArray(list) ? list : []).forEach((account) => {
      if (!account?.id) return;
      const role = String(account.role || "").toLowerCase();
      const isShellRole =
        role === "operating" ||
        role === "overdraft_reserve" ||
        role === "program_reserve";
      if (account.isVirtual === true || isShellRole) {
        ids.add(String(account.id).trim());
      }
    });
  };
  consider(options?.destinationAccounts);
  consider(options?.virtualAccounts);
  consider(options?.sourceAccounts);
  return ids;
};

export const fetchBaasAccountHierarchy = async (instance) => {
  if (!instance) return emptyHierarchy();

  const clientName = instance.displayName || instance.name || "";
  try {
    const options = await fetchInternalTransferOptions(instance);
    return buildBaasAccountHierarchyFromTransferOptions(options, { clientName });
  } catch {
    return {
      ...emptyHierarchy(),
      clientName,
    };
  }
};

const mapPeerPinnedBalances = (balances = []) =>
  (Array.isArray(balances) ? balances : [])
    .map((item) => {
      const currency = String(item.currency_code || item.currency || "").toUpperCase();
      if (!currency) return null;
      return {
        currency,
        available: Number(item.available ?? 0) / 100,
        blocked: Number(item.blocked ?? 0) / 100,
      };
    })
    .filter(Boolean);

const emptyCurrencyMap = () => new Map();

const addPeerBalancesToMap = (byCurrency, balances = []) => {
  (balances || []).forEach((balance) => {
    const currency = String(balance.currency || "").toUpperCase();
    if (!currency) return;
    const previous = byCurrency.get(currency) || {
      currency,
      available: 0,
      blocked: 0,
    };
    byCurrency.set(currency, {
      currency,
      available: previous.available + Number(balance.available || 0),
      blocked: previous.blocked + Number(balance.blocked || 0),
    });
  });
};

const hierarchyAccountFromTotals = (type, role, byCurrency) => ({
  type,
  id: `custody-${role}`,
  label: role,
  status: "active",
  role,
  balances: [...byCurrency.values()],
});

/**
 * Sum pinned Root / Operating / Overdraft / Program balances from
 * `GET v1/accounts/peer-balances` (already mapped to major units).
 * Shared pins are already deduped by the API.
 */
export const buildBaasCustodyHierarchyFromPeerBalances = (items = []) => {
  const byRole = {
    root: emptyCurrencyMap(),
    operating: emptyCurrencyMap(),
    overdraft_reserve: emptyCurrencyMap(),
    program_reserve: emptyCurrencyMap(),
  };

  (Array.isArray(items) ? items : []).forEach((item) => {
    const role = String(item?.role || "").toLowerCase();
    if (!byRole[role]) return;
    addPeerBalancesToMap(byRole[role], item.balances);
  });

  return {
    clientName: "",
    rootAccount: hierarchyAccountFromTotals(
      BAAS_ACCOUNT_TYPES.ROOT,
      "root",
      byRole.root,
    ),
    subAccounts: [
      hierarchyAccountFromTotals(
        BAAS_ACCOUNT_TYPES.OPERATIONAL,
        "operating",
        byRole.operating,
      ),
      hierarchyAccountFromTotals(
        BAAS_ACCOUNT_TYPES.OVERDRAFT,
        "overdraft_reserve",
        byRole.overdraft_reserve,
      ),
      hierarchyAccountFromTotals(
        BAAS_ACCOUNT_TYPES.RESERVE,
        "program_reserve",
        byRole.program_reserve,
      ),
    ],
  };
};

/**
 * Valut matrix only: GET `v1/accounts/peer-balances` — pinned slot balances of
 * every active instance (including the matrix). Empty for partners.
 */
export const fetchBaasPeerInstanceBalances = async (instance, options = {}) => {
  if (!instance || !isValutSegregationWalletInstance(instance)) return [];

  try {
    const query = {};
    if (options.instanceId != null && Number.isFinite(Number(options.instanceId))) {
      query.instance_id = Number(options.instanceId);
    }
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: "v1/accounts/peer-balances",
      query,
    });
    const items = Array.isArray(data?.items) ? data.items : [];
    return items
      .map((item) => {
        const accountId = String(item?.account_id || item?.accountId || "").trim();
        const role = String(item?.role || "").toLowerCase();
        if (!accountId || !role) return null;
        return {
          instanceId: item?.instance_id ?? item?.instanceId ?? null,
          instanceName: item?.instance_name || item?.instanceName || "",
          role,
          accountId,
          balances: mapPeerPinnedBalances(item?.balances),
        };
      })
      .filter(Boolean);
  } catch (error) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[baas] GET /v1/accounts/peer-balances failed", error);
    }
    return [];
  }
};

/** @deprecated Prefer fetchBaasAccountHierarchy */
export const getMockBaasAccountHierarchy = () => emptyHierarchy();
